/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

export type Header = { [key: string]: string | null };

export type FetchOptions = {
  method?: 'GET' | 'POST';
  variables?: { [key: string]: any };
  signal?: AbortSignal;
  cache?:
    | 'default'
    | 'no-store'
    | 'reload'
    | 'no-cache'
    | 'force-cache'
    | 'only-if-cached';
};

export type FetchQueryError = Array<{
  message: string;
  extensions: { category: string };
}>;

export type BeforeHook = (requestInit: RequestInit) => RequestInit;
export type AfterHook<T = any> = (
    requestInit: RequestInit,
    response: { errors?: FetchQueryError; data: T }
) => { errors?: FetchQueryError; data: T };

const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

class FetchGraphQLMesh {
  public _endpoint?: string;

  get endpoint() {
    return this._endpoint;
  }

  get fetchGraphQlHeaders() {
    return this._fetchGraphQlHeaders;
  }

  public _fetchGraphQlHeaders: Header = {};

  public _beforeHooks: BeforeHook[] = [];

  public _afterHooks: AfterHook[] = [];

  /**
   * Sets the GraphQL endpoint.
   * @param endpoint - The GraphQL endpoint.
   */
  public setEndpoint(endpoint: string) {
    this._endpoint = endpoint;
  }

  /**
   * Sets the GraphQL headers.
   * @param key - The key of the header.
   * @param value - The value of the header.
   */
  public setFetchGraphQlHeader(key: string, value: string | null) {
    this._fetchGraphQlHeaders = {
      ...this.fetchGraphQlHeaders,
      [key]: value,
    };
  }
  /**
   * Removes a specific GraphQL header.
   * @param key - The key of the header.
   */
  public removeFetchGraphQlHeader(key: string) {
    delete this._fetchGraphQlHeaders[key];
  }
  /**
   * Sets the GraphQL headers.
   * @param header - The header object or a function that returns a header object.
   * If a function is provided, it will be called with the previous headers.
   * The returned object will be merged with the previous headers.
   * @example
   * ```js
   * // set headers
   * setFetchGraphQlHeaders({ test: 'test' });
   * 
   * // merge with previous headers
   * setFetchGraphQlHeaders((prev) => ({
   *   ...prev,
   *  test: 'test2',
   * }));
   * ```
   */
  public setFetchGraphQlHeaders(header: Header | ((prev: Header) => Header)) {
    if (typeof header === 'function') {
      this._fetchGraphQlHeaders = {
        ...this._fetchGraphQlHeaders,
        ...header(this._fetchGraphQlHeaders),
      };
    } else {
      this._fetchGraphQlHeaders = { ...header };
    }
  }
  /**
   * Adds a hook executed before the GraphQL call.
   * @param hook - The hook function.
   * @example
   * ```js
   * // add before hook
   * addBeforeHook((requestInit) => console.log('About to execute ' + requestInit.method + ' call.'));
   *
   * // modify the requestInit before executing the request
   * addBeforeHook((requestInit) => {method: requestInit.method, body: 'new body'});
   * ```
   */
  public addBeforeHook(hook: BeforeHook): void {
    this._beforeHooks.push(hook);
  }
  /**
   * Adds a hook executed before the GraphQL call.
   * @param hook - The hook function.
   * @example
   * ```js
   * // add before hook
   * addAfterHook((requestInit, response) => console.log(
   *     'The result of ' + requestInit.method + ' call is ' + response.json().body
   * ));
   *
   * // modify the response
   * addAfterHook((requestInit, response) => new Response(JSON.stringify({ ...response, modified: true }));
   * ```
   */
  public addAfterHook(hook: AfterHook): void {
    this._afterHooks.push(hook);
  }
  /**
   * Fetches GraphQL data.
   * @param query - The GraphQL query.
   * @param options - Optional configuration for the fetch request.
   * @returns
   */
  public async fetchGraphQl<T = any>(
    query: string,
    options?: FetchOptions
  ): Promise<{ errors?: FetchQueryError; data: T }> {
    const endpoint = this.endpoint;
    const fetchGraphQlHeaders = this.fetchGraphQlHeaders;

    if (!endpoint) throw Error('Missing "url"');

    const method = options?.method ?? 'POST';
    const cache = options?.cache;
    const signal = options?.signal;

    let body;
    const url = new URL(endpoint);
    const headers = {
      ...defaultHeaders,
      ...fetchGraphQlHeaders,
    };

    if (method === 'POST') {
      body = JSON.stringify({
        query,
        variables: options?.variables,
      });
    }

    if (method === 'GET') {
      url.searchParams.append('query', minimizeGraphQlQuery(query));

      if (options?.variables)
        url.searchParams.append('variables', JSON.stringify(options.variables));
    }

    let requestInit: RequestInit = {
      method,
      headers,
      body,
      cache,
      signal,
    };

    requestInit = await this._beforeHooks.reduce(
        async (prev, hook) => hook(await prev),
        Promise.resolve(requestInit)
    );

    return await fetch(url, requestInit).then((r) => r.json().then(
        (response) => this._afterHooks.reduce(
            async (result, hook) => hook(requestInit, await result),
            Promise.resolve(response)
        )
    ));
  }
  /**
   * Gets the configuration.
   */
  public getConfig() {
    return {
      endpoint: this.endpoint,
      fetchGraphQlHeaders: this.fetchGraphQlHeaders,
    };
  }

  public getMethods() {
    return {
      setEndpoint: this.setEndpoint.bind(this),
      setFetchGraphQlHeader: this.setFetchGraphQlHeader.bind(this),
      removeFetchGraphQlHeader: this.removeFetchGraphQlHeader.bind(this),
      setFetchGraphQlHeaders: this.setFetchGraphQlHeaders.bind(this),
      fetchGraphQl: this.fetchGraphQl.bind(this),
      getConfig: this.getConfig.bind(this),
      addBeforeHook: this.addBeforeHook.bind(this),
      addAfterHook: this.addAfterHook.bind(this),
    };
  }
}

const mesh = new FetchGraphQLMesh();

/**
 * `FetchGraphQL` is a class that extends `FetchGraphQLMesh`.
 * It provides methods to get the GraphQL endpoint and headers.
 *
 * @class
 *
 */
export class FetchGraphQL extends FetchGraphQLMesh {
  get endpoint() {
    return this._endpoint ?? mesh.endpoint;
  }

  get fetchGraphQlHeaders() {
    return (
      (this._endpoint
        ? this._fetchGraphQlHeaders
        : { ...this._fetchGraphQlHeaders, ...mesh.fetchGraphQlHeaders })
    );
  }
}

function minimizeGraphQlQuery(query: string) {
  // Remove comments
  query = query.replace(/#.*/g, '');

  // Remove extra spaces, tabs, and line breaks
  query = query.replace(/\s+/g, ' ');

  return query.trim();
}
/**
 * Exports several methods from the `mesh` object.
 *
 * @property {Function} setEndpoint - Sets the GraphQL endpoint.
 * @property {Function} setFetchGraphQlHeaders - Sets the GraphQL headers.
 * @property {Function} setFetchGraphQlHeader - Sets a specific GraphQL header.
 * @property {Function} removeFetchGraphQlHeader - Removes a specific GraphQL header.
 * @property {Function} fetchGraphQl - Fetches GraphQL data.
 * @property {Function} getConfig - Gets the configuration.
 */

// Global Mesh instance
export const {
  setEndpoint,
  setFetchGraphQlHeaders,
  setFetchGraphQlHeader,
  removeFetchGraphQlHeader,
  fetchGraphQl,
  getConfig,
  addBeforeHook,
  addAfterHook,
} = mesh.getMethods();
